import i18n from 'i18next'
import HttpBackend from 'i18next-http-backend'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, isSupportedLocale } from './locales'

/**
 * Per-module namespace list. Mirrors the cross-platform module boundary
 * (iOS modules + Android modules + Metro V2 web modules). Each module
 * owns its catalog at `public/locales/<lng>/<ns>.json`; `common` carries
 * site-wide / cross-module strings (titles, meta descriptions, generic
 * UI, cookie banner).
 *
 * Naming inside each namespace: **flat snake_case keys** per the
 * cross-platform contract (`metro/wiki/contracts/string-keys-v2.csv`).
 *
 *   const { t } = useTranslation('route-card')
 *   t('route_card_build_route')          // flat key, namespace-local
 *
 * Or cross-namespace:
 *
 *   const { t } = useTranslation(['route-card', 'common'])
 *   t('route-card:route_card_build_route')
 *   t('common:ui.search.placeholder')        // 'common' allows dotted keys for legacy structure
 */
export const NAMESPACES = [
  'common',
  'feedback',
  'line-details',
  'route-card',
  'route-details',
  'station-card',
  'station-details',
  'station-page',
  'suggester',
] as const

/**
 * i18next bootstrap for the SPA. The prerendered HTML is locale-frozen at
 * build time (one HTML per /:locale/) — this runtime layer drives the
 * SPA's client-side strings and reactive language switching for users
 * who navigate between locale prefixes without a full reload.
 *
 * Resolution order at runtime:
 *
 *   1. Path detector — the `/:locale/...` segment is authoritative.
 *      `installI18n()` (see `install.ts`) reads `location.pathname` before
 *      this config picks a fallback.
 *   2. `localStorage` (`metro-locale`) — user's explicit picker choice.
 *   3. `navigator.language` — system / browser default.
 *   4. `DEFAULT_LOCALE` — `en` (canonical, also x-default in hreflang).
 *
 * Catalogs are fetched from `/locales/<lng>/<ns>.json` (served from
 * `public/`). HTTP backend, not bundled, so the production deploy can
 * swap a catalog without re-cutting the SPA bundle — important when
 * SEO + linguist briefs land in iter-2 and we want hot-fix paths.
 */
void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    fallbackLng: DEFAULT_LOCALE,
    ns: NAMESPACES as unknown as string[],
    defaultNS: 'common',
    fallbackNS: 'common',
    load: 'currentOnly',
    nonExplicitSupportedLngs: false,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupFromPathIndex: 0,
      lookupLocalStorage: 'metro-locale',
      caches: ['localStorage'],
    },
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    react: {
      useSuspense: false,
    },
  })

export { i18n }

/**
 * Reads the path-segment locale at module load time. Used by the App
 * shell to render the right `<html lang>` and seed i18next before the
 * detector chain runs (avoids a flash of fallback strings on first
 * paint when the user is on `/ru/...`).
 */
export function readLocaleFromPath(pathname: string): string | null {
  const seg = pathname.split('/').filter(Boolean)[0] ?? null
  if (seg === null) return null
  if (!isSupportedLocale(seg)) return null
  return seg
}
